/**
 * src/components/agent/DictationRestoreBar.jsx — TICKET-936.
 *
 * Shows the newest dictation that is no longer in the composer and gives it
 * back in one tap. Discard needs a second tap: it is the only way the text is
 * thrown away without being sent.
 *
 * Props: safety (useDictationSafetyNet), onRestore(text), busy (recording or transcribing)
 */
import { useState, useEffect } from 'react';
import { Undo2, Trash2, Mic } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '8px 12px',
  borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  background: 'var(--card)', color: 'var(--t1)', boxShadow: 'inset 0 0 0 1px var(--bdr)',
};

export default function DictationRestoreBar({ safety, onRestore, busy = false }) {
  const { t } = useT();
  const entry = safety?.missing?.[0] || null;
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { setConfirming(false); }, [entry?.id]);
  if (!entry || busy) return null;
  const more = safety.missing.length - 1;

  return (
    <div data-testid="dictation-restore-bar" style={{
      marginTop: 8, padding: '10px 12px', borderRadius: 10,
      background: 'var(--accent-bg)', boxShadow: 'inset 0 0 0 1px var(--bdr)',
    }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8, minWidth: 0 }}>
        <Mic size={16} strokeWidth={1.75} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>
            {t('dictation.restoreTitle')}{more > 0 ? ` (+${more})` : ''}
          </div>
          <div data-testid="dictation-restore-text" style={{
            fontSize: 12, color: 'var(--t2)', lineHeight: 1.4, overflow: 'hidden',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>{entry.text}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" data-testid="dictation-restore-apply"
          onClick={() => { onRestore?.(entry.text); safety.restored?.(entry.id); }}
          style={{ ...btn, background: 'var(--accent)', color: 'var(--on-accent)' }}>
          <Undo2 size={15} strokeWidth={1.75} />{t('dictation.restore')}
        </button>
        <button type="button" data-testid="dictation-restore-discard"
          onClick={() => { if (!confirming) { setConfirming(true); return; } setConfirming(false); safety.discard(entry.id); }}
          style={{ ...btn, color: confirming ? 'var(--bad)' : 'var(--t2)' }}>
          <Trash2 size={15} strokeWidth={1.75} />{confirming ? t('dictation.discardConfirm') : t('dictation.discard')}
        </button>
      </div>
    </div>
  );
}
